// Deal stage changes + the lost-reason rollup.
//
// WHY THIS EXISTS: until 2026-08-03 Opportunity was render-only in this CRM —
// stage badges and nothing else — so stage changes happened in the Salesforce
// UI and nobody was ever asked WHY a deal died. Casey parked two pricing
// changes that day (deepening the multi-year discount ladder in squish-backend
// lib/billing-terms.js, and adding an entry-level VPS in the $15-35 band)
// pending evidence that entry price is what costs us deals. That evidence is
// this rollup, and it only exists if the reason gets captured at close time.
//
// The picklist values and the stage list both come from the SERVER
// (GET /api/opportunities/stages), never from literals here — stages are
// editable in Salesforce Setup and the reasons are a restricted picklist in
// squish-docs/salesforce-metadata/objects/Opportunity.object. A hardcoded copy
// in the UI would be the half that goes stale.

import React, { useEffect, useState } from 'react';
import { fetchOpportunityStages, updateOpportunityStage, fetchLostReasons } from '../api';

const fmt$ = (v) => (v == null ? '—' : `$${parseFloat(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);

const backdrop = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const panel = { width: '100%', maxWidth: 460, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', flexDirection: 'column', overflow: 'hidden' };
const header = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 18px', borderBottom: '1px solid var(--border)' };
const body = { padding: 18, display: 'flex', flexDirection: 'column', gap: 14 };
const footer = { padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 10 };
const label = { fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6, letterSpacing: '0.5px' };

// Opaque background is load-bearing, not cosmetic. Chrome paints a native
// select's dropdown from the element's OWN background — a translucent value
// renders the open popup white-on-white in dark mode. --bg3 is a solid hex in
// both themes.
const select = { width: '100%', padding: '8px 10px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)', boxSizing: 'border-box' };
const textarea = { ...select, minHeight: 72, resize: 'vertical' };

export function StageChangeModal({ opp, onClose, onSaved }) {
  const [meta, setMeta]       = useState(null);
  const [stage, setStage]     = useState(opp?.StageName || '');
  const [reason, setReason]   = useState('');
  const [notes, setNotes]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [loadErr, setLoadErr] = useState('');

  useEffect(() => {
    let alive = true;
    fetchOpportunityStages()
      .then(m => { if (alive) setMeta(m); })
      .catch(e => { if (alive) setLoadErr(e.message); });
    return () => { alive = false; };
  }, []);

  const lostStage = meta?.lostStage || 'Closed Lost';
  const isLost = stage === lostStage;
  // The gate. No reason, no save — and the server and a Salesforce validation
  // rule both re-check, so this is convenience, not the only guard.
  const canSave = !!stage && stage !== opp?.StageName && (!isLost || !!reason) && !saving;

  async function save() {
    setSaving(true); setError('');
    try {
      await updateOpportunityStage(opp.Id, { StageName: stage, reason: isLost ? reason : null, notes: isLost ? notes : null });
      onSaved?.({ ...opp, StageName: stage });
      onClose?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={header}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Change deal stage</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 2 }}>
              {opp?.Name} · {fmt$(opp?.Amount)}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
        </div>

        <div style={body}>
          {loadErr && (
            <div style={{ fontSize: 12, color: '#f87171', fontFamily: 'var(--mono)' }}>
              Couldn't load stages: {loadErr}
            </div>
          )}

          <div>
            <div style={label}>Stage</div>
            <select style={select} value={stage} onChange={e => { setStage(e.target.value); setError(''); }} disabled={!meta}>
              {!meta && <option>Loading…</option>}
              {(meta?.stages || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {isLost && (
            <>
              <div>
                <div style={label}>Why did we lose it? <span style={{ color: '#f87171' }}>*</span></div>
                <select style={select} value={reason} onChange={e => { setReason(e.target.value); setError(''); }}>
                  <option value="">Select a reason…</option>
                  {(meta?.lostReasons || []).map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <div style={label}>
                  Detail {reason === 'Price' || reason === 'Other' ? <span style={{ color: 'var(--badge-yellow)' }}>— strongly recommended</span> : '(optional)'}
                </div>
                <textarea
                  style={textarea}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder={reason === 'Price'
                    ? 'What price did they expect, and against whom? A bare "lost on price" count has no target to aim at.'
                    : 'Competitor named, capability asked for, timing constraint…'}
                />
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, borderLeft: '2px solid var(--border)', paddingLeft: 10 }}>
                This is the input to the parked pricing decisions — the multi-year
                discount ladder and an entry-level VPS both stay as they are until
                these reasons say otherwise. Guessing here is worse than leaving a deal open.
              </div>
            </>
          )}

          {error && (
            <div style={{ fontSize: 12, color: '#f87171', fontFamily: 'var(--mono)', lineHeight: 1.5 }}>{error}</div>
          )}
        </div>

        <div style={footer}>
          <button onClick={onClose} style={{ padding: '7px 14px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text2)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--mono)' }}>Cancel</button>
          <button data-write
            onClick={save}
            disabled={!canSave}
            style={{ padding: '7px 14px', background: canSave ? 'var(--accent)' : 'var(--bg3)', border: 'none', borderRadius: 6, color: canSave ? '#fff' : 'var(--text3)', fontSize: 12, fontWeight: 700, cursor: canSave ? 'pointer' : 'not-allowed', fontFamily: 'var(--mono)' }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Lost-reason rollup ──────────────────────────────────────────────────────
// The one-glance answer to "how many did we lose on price".
//
// Renders coverage as prominently as the split, on purpose. A column that is
// mostly empty can produce a very confident-looking "Price: 100%" off a single
// record, and this panel is the evidence surface for a real pricing decision —
// the failure mode to design against is absent data reading as good news.

const WINDOWS = [
  { label: '12 mo', months: 12 },
  { label: '24 mo', months: 24 },
  { label: 'All',   months: null },
];

export function LostReasonsPanel() {
  const [months, setMonths] = useState(12);
  const [data, setData]     = useState(null);
  const [err, setErr]       = useState('');
  const [loading, setLoad]  = useState(true);

  useEffect(() => {
    let alive = true;
    setLoad(true); setErr('');
    fetchLostReasons({ months })
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setErr(e.message); })
      .finally(() => { if (alive) setLoad(false); });
    return () => { alive = false; };
  }, [months]);

  const max = Math.max(1, ...((data?.byReason || []).map(r => r.count)));

  return (
    <div className="table-card">
      <div className="table-header">
        <span className="table-title">Why We Lose</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {WINDOWS.map(w => (
            <button
              key={w.label}
              onClick={() => setMonths(w.months)}
              style={{
                padding: '3px 9px', borderRadius: 5, fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer',
                background: months === w.months ? 'var(--accent)' : 'transparent',
                color: months === w.months ? '#fff' : 'var(--text3)',
                border: `1px solid ${months === w.months ? 'var(--accent)' : 'var(--border)'}`,
              }}
            >{w.label}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {loading && <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 12, fontFamily: 'var(--mono)', padding: 20 }}>Loading…</div>}

        {!loading && err && (
          <div style={{ color: '#f87171', fontSize: 12, fontFamily: 'var(--mono)' }}>{err}</div>
        )}

        {/* Field not deployed. Deliberately NOT rendered as zeros — "we have not
            measured" and "we lost nobody on price" point at opposite decisions. */}
        {!loading && !err && data && !data.available && (
          <div style={{ fontSize: 12, color: 'var(--badge-yellow)', lineHeight: 1.6, fontFamily: 'var(--mono)' }}>
            Not being measured yet — the lost-reason field isn't deployed to Salesforce.
            <div style={{ color: 'var(--text3)', marginTop: 4 }}>
              Deploy squish-docs/salesforce-metadata, then this fills in. Until it does, treat "we aren't losing on price" as unknown, not false.
            </div>
          </div>
        )}

        {!loading && !err && data?.available && data.totalLost === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 12, fontFamily: 'var(--mono)', padding: 20 }}>
            No deals closed lost in this window
          </div>
        )}

        {!loading && !err && data?.available && data.totalLost > 0 && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {data.byReason.filter(r => r.count > 0).map(r => (
                <div key={r.reason} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 130, fontSize: 12, color: 'var(--text2)', flexShrink: 0 }}>{r.reason}</div>
                  <div style={{ flex: 1, height: 16, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{
                      width: `${(r.count / max) * 100}%`, height: '100%', borderRadius: 3,
                      background: r.reason === 'Price' ? 'var(--badge-yellow)' : 'var(--accent)',
                    }} />
                  </div>
                  <div style={{ width: 92, textAlign: 'right', fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', flexShrink: 0 }}>
                    {r.count} · {r.pctOfLost}% · {fmt$(r.value)}
                  </div>
                </div>
              ))}
              {data.byReason.every(r => r.count === 0) && (
                <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                  {data.totalLost} lost, none with a reason recorded.
                </div>
              )}
            </div>

            {/* Coverage sits with the numbers, not in a footnote. */}
            <div style={{
              marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)',
              fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', lineHeight: 1.6,
            }}>
              {data.totalLost} lost · {data.coveragePct}% have a reason
              {data.unspecified > 0 && (
                <span style={{ color: 'var(--badge-yellow)' }}> · {data.unspecified} unrecorded</span>
              )}
              {data.coveragePct < 70 && (
                <div style={{ color: 'var(--badge-yellow)', marginTop: 4 }}>
                  Coverage is thin — this split isn't yet strong enough to move a pricing decision on.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
